import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import { ApiError, apiRequest } from "../../api/client";
import type { components } from "../../api/generated/schema";
import {
  ConfirmModal,
  EditorModal,
  ErrorDialog,
  ErrorText
} from "../catalog/CatalogPages";
import { ContextHeader } from "../shared/project-context";
import { demoClearSummary } from "../projects/useDemoDataClear";
import { AssistantMultiSelect } from "../shared/AssistantMultiSelect";
import { CheckboxMultiSelect } from "../shared/CheckboxMultiSelect";
import { TeacherSearchSelect } from "../shared/TeacherSearchSelect";
import {
  type WeekPattern,
  weekPatternLabel,
  weekPatternOptions
} from "../shared/weekPatterns";
import type { PlanningSummary } from "./PlanningPage";
import { PlanningImportActions } from "./PlanningImportActions";
import {
  AvailableTimeSlotsField,
  availableTimeSummary,
  isAvailableTimeSelectionValid,
  normalizeAvailableTimeSelection,
  type AvailableTimeCell,
  type AvailableTimeCellReference,
  type AvailableSlotOption,
  type AvailableSlotReference,
  type TimePreference
} from "./AvailableTimeSlotsField";
import { CourseTimeRulesPanel } from "./CourseTimeRulesPanel";
import {
  buildTeacherWorkloadLabels,
  TeacherWorkloadPanel,
  type TeacherWorkloadSource
} from "./TeacherWorkloads";
import {
  planDemoMessage,
  usePlanDemoData
} from "./usePlanDemoData";
import "./plans.css";

interface PlanningReference {
  id: string;
  label: string;
  active: boolean;
}

interface Allocation {
  id: string;
  requirement_id: string;
  main_teacher: PlanningReference;
  assistant_teachers: PlanningReference[];
  weekly_lessons: number;
  weekly_lessons_min: number;
  weekly_lessons_max: number;
  week_pattern: WeekPattern;
  sort_order: number;
  invalid_fields: string[];
}
type ClassList = components["schemas"]["ClassListResponse"];
type CompilationPreview =
  components["schemas"]["CompilationPreviewResponse"];
type CompilationResponse = components["schemas"]["CompilationResponse"];
type CourseList = components["schemas"]["CourseListResponse"];
interface RequirementBatchResponse {
  created_requirement_ids: string[];
  skipped_duplicates: unknown[];
}
interface Requirement extends Omit<
  components["schemas"]["RequirementResponse"],
  "allocations" | "week_pattern"
> {
  allocations: Allocation[];
  week_pattern: WeekPattern;
  allocated_lessons_min: number;
  allocated_lessons_max: number;
  available_time_slots: AvailableSlotReference[] | null;
  available_time_cells: AvailableTimeCellReference[] | null;
  non_adjacent_course_hints: PlanningReference[];
}

interface PlanningDraftResponse {
  stage: "FOUNDATION" | "DETAIL";
  condition_revision: number;
  locked: boolean;
  items: Requirement[];
}
type RoomList = components["schemas"]["RoomListResponse"];
type TeacherList = components["schemas"]["TeacherListResponse"];

interface RequirementForm {
  classId: string;
  classIds: string[];
  courseId: string;
  courseIds: string[];
  weeklyLessons: number;
  weekPattern: WeekPattern;
  consecutive: boolean;
  allowMultiple: boolean;
  roomTypeId: string;
  timePreference: TimePreference;
  availableTimeCells: AvailableTimeCell[] | null;
}

interface TimeSlotList {
  items: AvailableSlotOption[];
  condition_revision: number;
  locked: boolean;
}

interface AllocationForm {
  mainTeacherId: string;
  assistantTeacherIds: string[];
  weeklyLessonsMin: number;
  weeklyLessonsMax: number;
}

interface QuickRule extends Omit<RequirementForm, "classId" | "classIds"> {
  key: string;
}

interface TeacherForm {
  mainTeacherId: string;
  assistantTeacherIds: string[];
  courseIds: string[];
  scope: "GRADE" | "CLASSES";
  gradeCodes: string[];
  classIds: string[];
  weeklyLessonsMin: string;
  weeklyLessonsMax: string;
}

interface RoomBatchForm {
  requirementIds: string[];
  roomTypeId: string;
}

interface WriteCommand {
  path: string;
  method: "POST" | "PATCH" | "DELETE";
  body?: object;
  successMessage: string;
}

type DeleteTarget =
  | { kind: "requirement"; requirement: Requirement }
  | {
      kind: "allocation";
      requirement: Requirement;
      allocation: Allocation;
    };

function emptyRequirement(classId = ""): RequirementForm {
  return {
    classId,
    classIds: classId ? [classId] : [],
    courseId: "",
    courseIds: [],
    weeklyLessons: 1,
    weekPattern: "EVERY",
    consecutive: false,
    allowMultiple: false,
    roomTypeId: "",
    timePreference: "ANY",
    availableTimeCells: null
  };
}

function emptyQuickRule(): QuickRule {
  return { ...emptyRequirement(), key: crypto.randomUUID() };
}

const emptyTeacherForm: TeacherForm = {
  mainTeacherId: "",
  assistantTeacherIds: [],
  courseIds: [],
  scope: "GRADE",
  gradeCodes: [],
  classIds: [],
  weeklyLessonsMin: "",
  weeklyLessonsMax: ""
};

export function PlanPreparationPage({
  projectId,
  summary
}: {
  projectId: string;
  summary: PlanningSummary;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [selectedGrade, setSelectedGrade] = useState("ALL");
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedTeacher, setSelectedTeacher] = useState("ALL");
  const [selectedCourse, setSelectedCourse] = useState("ALL");
  const [quickGrades, setQuickGrades] = useState<string[]>([]);
  const [quickRules, setQuickRules] = useState<QuickRule[]>([
    emptyQuickRule()
  ]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [requirementEditing, setRequirementEditing] = useState<
    Requirement | "new" | null
  >(null);
  const [requirementForm, setRequirementForm] =
    useState<RequirementForm>(emptyRequirement());
  const [requirementDialogError, setRequirementDialogError] =
    useState<unknown>(null);
  const [allocationEditing, setAllocationEditing] = useState<{
    requirement: Requirement;
    allocation: Allocation | null;
  } | null>(null);
  const [allocationForm, setAllocationForm] =
    useState<AllocationForm>({
      mainTeacherId: "",
      assistantTeacherIds: [],
      weeklyLessonsMin: 1,
      weeklyLessonsMax: 1
    });
  const [teacherOpen, setTeacherOpen] = useState(false);
  const [teacherForm, setTeacherForm] =
    useState<TeacherForm>(emptyTeacherForm);
  const [roomBatchOpen, setRoomBatchOpen] = useState(false);
  const [roomBatchForm, setRoomBatchForm] = useState<RoomBatchForm>({
    requirementIds: [],
    roomTypeId: ""
  });
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [previewResult, setPreviewResult] =
    useState<CompilationPreview | null>(null);
  const [notice, setNotice] = useState(() => {
    const key = `planning-demo-applied:${projectId}`;
    const message = sessionStorage.getItem(key) ?? "";
    sessionStorage.removeItem(key);
    return message;
  });
  // 阶段一内部步骤：仅首次数据到达时按进度推导一次，之后完全由用户切换
  const [step, setStep] = useState<1 | 2 | 3 | null>(null);
  const [focusRequirementId, setFocusRequirementId] =
    useState<string | null>(null);
  const [pinnedRequirementOrder, setPinnedRequirementOrder] = useState<
    string[] | null
  >(null);

  const draft = useQuery({
    queryKey: ["planning-draft", projectId],
    queryFn: () =>
      apiRequest<PlanningDraftResponse>(
        `/projects/${projectId}/planning/draft`
      )
  });
  const courses = useQuery({
    queryKey: ["courses", projectId],
    queryFn: () =>
      apiRequest<CourseList>(`/projects/${projectId}/courses`)
  });
  const teachers = useQuery({
    queryKey: ["teachers", projectId],
    queryFn: () =>
      apiRequest<TeacherList>(`/projects/${projectId}/teachers`)
  });
  const classes = useQuery({
    queryKey: ["classes", projectId],
    queryFn: () =>
      apiRequest<ClassList>(`/projects/${projectId}/classes`)
  });
  const rooms = useQuery({
    queryKey: ["rooms", projectId],
    queryFn: () =>
      apiRequest<RoomList>(`/projects/${projectId}/room-types`)
  });
  const slots = useQuery({
    queryKey: ["time-slots", projectId],
    queryFn: () =>
      apiRequest<TimeSlotList>(`/projects/${projectId}/time-slots`)
  });

  async function refreshDraft(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["planning-draft", projectId] }),
      queryClient.invalidateQueries({ queryKey: ["planning", projectId] }),
      queryClient.invalidateQueries({ queryKey: ["project", projectId] })
    ]);
    await queryClient.refetchQueries({
      queryKey: ["planning-draft", projectId],
      exact: true,
      type: "active"
    });
  }

  const write = useMutation({
    mutationFn: async (command: WriteCommand) => ({
      command,
      result: await apiRequest<unknown>(command.path, {
        method: command.method,
        body: command.body ? JSON.stringify(command.body) : undefined
      })
    }),
    onSuccess: async ({ command, result }) => {
      let message = command.successMessage;
      if (command.path.endsWith("/requirements/batch")) {
        const response = result as RequirementBatchResponse;
        message += `：新增 ${response.created_requirement_ids.length} 项`;
        if (response.skipped_duplicates.length > 0) {
          message += `；另有 ${response.skipped_duplicates.length} 项已存在，未覆盖`;
        }
      }
      setNotice(message);
      setPreviewResult(null);
      await refreshDraft();
      setQuickOpen(false);
      setRequirementEditing(null);
      setRequirementDialogError(null);
      setAllocationEditing(null);
      setTeacherOpen(false);
      setRoomBatchOpen(false);
      setDeleteTarget(null);
    },
    onError: async (error) => {
      if (requirementEditing) setRequirementDialogError(error);
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        setNotice("其他窗口已更新这些规则。已加载最新版本，请复核当前表单后再提交。");
        await refreshDraft();
      }
    }
  });
  const preview = useMutation({
    mutationFn: () =>
      apiRequest<CompilationPreview>(
        `/projects/${projectId}/planning/preview`,
        {
          method: "POST",
          body: JSON.stringify({
            expected_condition_revision: draft.data?.condition_revision
          })
        }
      ),
    onSuccess: (result) => {
      setPreviewResult(result);
      setNotice(
        result.ready
          ? `检查通过：将生成 ${result.plan_count} 条详细计划，共 ${result.total_lessons} 节周课时。`
          : `检查完成：发现 ${result.issues.length} 个需要完善的问题。`
      );
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await refreshDraft();
      }
    }
  });
  const compile = useMutation({
    mutationFn: () =>
      apiRequest<CompilationResponse>(
        `/projects/${projectId}/planning/compile`,
        {
          method: "POST",
          body: JSON.stringify({
            expected_condition_revision: draft.data?.condition_revision
          })
        }
      ),
    onSuccess: async (result) => {
      sessionStorage.setItem(
        `planning-compiled:${projectId}`,
        `已生成 ${result.generated_plan_count} 条详细任课计划，可继续逐条调整。`
      );
      queryClient.removeQueries({
        queryKey: ["planning-draft", projectId],
        exact: true
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["planning", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["plans", projectId] }),
        queryClient.invalidateQueries({ queryKey: ["project", projectId] })
      ]);
    },
    onError: async (error) => {
      if (error instanceof ApiError && error.code === "STALE_VERSION") {
        await refreshDraft();
      }
    }
  });

  useEffect(() => {
    if (step !== null || !draft.data) return;
    const items = draft.data.items;
    if (items.length === 0) {
      setStep(1);
    } else if (
      items.some(
        (item) => item.missing_lessons !== 0 || item.allocations.length === 0
      )
    ) {
      setStep(2);
    } else {
      setStep(3);
    }
  }, [step, draft.data]);

  useEffect(() => {
    if (!focusRequirementId || step !== 2) return;
    const element = document.getElementById(
      `requirement-${focusRequirementId}`
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.focus({ preventScroll: true });
    }
    setFocusRequirementId(null);
  }, [focusRequirementId, step]);

  const revision = draft.data?.condition_revision ?? summary.condition_revision;
  const classItems = classes.data?.items ?? [];
  const courseItems = courses.data?.items ?? [];
  const teacherItems = teachers.data?.items ?? [];
  const roomItems = rooms.data?.items ?? [];
  const gradeOptions = Array.from(
    new Map(
      classItems.map((item) => [
        item.grade_code,
        { code: item.grade_code, label: item.grade_label }
      ])
    ).values()
  ).sort((left, right) => gradeSortValue(left.code) - gradeSortValue(right.code));
  const visibleClasses = classItems.filter(
    (item) =>
      selectedGrade === "ALL" || item.grade_code === Number(selectedGrade)
  );
  const requirements = draft.data?.items ?? [];
  const visibleRequirements = requirements.filter(
    (item) =>
      (selectedGrade === "ALL" ||
        item.school_class.grade_code === Number(selectedGrade)) &&
      (selectedClass === "ALL" || item.school_class.id === selectedClass) &&
      (selectedCourse === "ALL" || item.course.id === selectedCourse) &&
      (selectedTeacher === "ALL" ||
        item.allocations.some(
          (allocation) =>
            allocation.main_teacher.id === selectedTeacher ||
            allocation.assistant_teachers.some(
              (teacher) => teacher.id === selectedTeacher
            )
        ))
  );
  const incompleteFirst = pinnedRequirementOrder !== null;
  const pinnedOrderById = new Map(
    (pinnedRequirementOrder ?? []).map((id, index) => [id, index])
  );
  const orderedVisibleRequirements = pinnedRequirementOrder
    ? visibleRequirements
        .map((item, index) => ({ item, index }))
        .sort(
          (left, right) =>
            (pinnedOrderById.get(left.item.id) ??
              pinnedRequirementOrder.length + left.index) -
            (pinnedOrderById.get(right.item.id) ??
              pinnedRequirementOrder.length + right.index)
        )
        .map(({ item }) => item)
    : visibleRequirements;
  const loading =
    draft.isLoading ||
    courses.isLoading ||
    teachers.isLoading ||
    classes.isLoading ||
    rooms.isLoading ||
    slots.isLoading;
  const loadError =
    draft.error ?? courses.error ?? teachers.error ?? classes.error ?? rooms.error ?? slots.error;
  const planDemo = usePlanDemoData(
    projectId,
    revision,
    (result) => {
      setPreviewResult(null);
      setNotice(planDemoMessage(result));
      setStep(3);
    },
    undefined,
    (result) => {
      setPreviewResult(null);
      setNotice(
        result.counts.cleared > 0
          ? `已清理 ${result.counts.cleared} 条未编辑的任课计划模拟数据；人工改动、依赖项和来源不确定的数据均已保留。`
          : "没有可安全清理的任课计划模拟数据，现有数据未发生变化。"
      );
    }
  );
  const shouldEmphasizeRuleCheck =
    notice.startsWith("任课计划模拟数据已填入") && previewResult === null;

  function openQuick(): void {
    write.reset();
    setQuickGrades(gradeOptions[0] ? [String(gradeOptions[0].code)] : []);
    setQuickRules([emptyQuickRule()]);
    setQuickOpen(true);
  }

  function pinIncompleteRequirements(): void {
    setPinnedRequirementOrder(
      requirements
        .map((item, index) => ({ item, index }))
        .sort((left, right) => {
          const leftIncomplete =
            left.item.missing_lessons !== 0 || left.item.allocations.length === 0;
          const rightIncomplete =
            right.item.missing_lessons !== 0 || right.item.allocations.length === 0;
          return (
            Number(rightIncomplete) - Number(leftIncomplete) ||
            left.index - right.index
          );
        })
        .map(({ item }) => item.id)
    );
  }

  function submitQuick(): void {
    const selectedGradeCodes = new Set(quickGrades.map(Number));
    const selectedClasses = classItems.filter((item) =>
      selectedGradeCodes.has(item.grade_code)
    );
    write.mutate({
      path: `/projects/${projectId}/planning/requirements/batch`,
      method: "POST",
      body: {
        items: quickRules.flatMap((rule) =>
          selectedClasses.flatMap((schoolClass) =>
            rule.courseIds.map((courseId) => ({
              class_id: schoolClass.id,
              course_id: courseId,
              weekly_lessons: rule.weeklyLessons,
              week_pattern: rule.weekPattern,
              consecutive: rule.consecutive,
              allow_multiple_groups_per_day: rule.allowMultiple,
              room_type_id: rule.roomTypeId || null,
              time_preference: rule.timePreference,
              available_time_slot_ids: slotIdsFromCells(rule.availableTimeCells),
              available_time_cells: rule.availableTimeCells
            }))
          )
        ),
        expected_condition_revision: revision
      },
      successMessage: "年级规则已批量展开为具体班级课程需求"
    });
  }

  function openRequirement(item: Requirement | "new"): void {
    write.reset();
    setRequirementDialogError(null);
    setRequirementEditing(item);
    if (item === "new") {
      const defaultClass =
        selectedClass !== "ALL"
          ? selectedClass
          : visibleClasses[0]?.id ?? classItems[0]?.id ?? "";
      setRequirementForm(emptyRequirement(defaultClass));
      return;
    }
    const availableTime = normalizeAvailableTimeSelection(
      item.time_preference,
      item.available_time_cells,
      slots.data?.items ?? []
    );
    setRequirementForm({
      classId: item.school_class.active ? item.school_class.id : "",
      courseId: item.course.active ? item.course.id : "",
      weeklyLessons: item.weekly_lessons,
      weekPattern: item.week_pattern,
      consecutive: item.consecutive,
      allowMultiple: item.allow_multiple_groups_per_day,
      roomTypeId: item.room_type?.active ? item.room_type.id : "",
      timePreference: availableTime.timePreference,
      availableTimeCells: availableTime.availableTimeCells,
      classIds: [item.school_class.id],
      courseIds: [item.course.id]
    });
  }

  function submitRequirement(): void {
    if (!requirementEditing) return;
    setRequirementDialogError(null);
    const create = requirementEditing === "new";
    write.mutate({
      path: create
        ? `/projects/${projectId}/planning/requirements/batch`
        : `/projects/${projectId}/planning/requirements/${requirementEditing.id}`,
      method: create ? "POST" : "PATCH",
      body: create ? {
        items: requirementForm.classIds.flatMap((classId) =>
          requirementForm.courseIds.map((courseId) => ({
            class_id: classId,
            course_id: courseId,
            weekly_lessons: requirementForm.weeklyLessons,
            week_pattern: requirementForm.weekPattern,
            consecutive: requirementForm.consecutive,
            allow_multiple_groups_per_day: requirementForm.allowMultiple,
            room_type_id: requirementForm.roomTypeId || null,
            time_preference: requirementForm.timePreference,
            available_time_slot_ids: slotIdsFromCells(requirementForm.availableTimeCells),
            available_time_cells: requirementForm.availableTimeCells
          }))
        ),
        expected_condition_revision: revision
      } : {
        class_id: requirementForm.classId,
        course_id: requirementForm.courseId,
        weekly_lessons: requirementForm.weeklyLessons,
        week_pattern: requirementForm.weekPattern,
        consecutive: requirementForm.consecutive,
        allow_multiple_groups_per_day: requirementForm.allowMultiple,
        room_type_id: requirementForm.roomTypeId || null,
        time_preference: requirementForm.timePreference,
        available_time_slot_ids: slotIdsFromCells(requirementForm.availableTimeCells),
        available_time_cells: requirementForm.availableTimeCells,
        expected_condition_revision: revision
      },
      successMessage: create ? "班级课程需求已批量添加" : "班级课程需求已更新"
    });
  }

  function openAllocation(
    requirement: Requirement,
    allocation: Allocation | null
  ): void {
    write.reset();
    setAllocationEditing({ requirement, allocation });
    setAllocationForm(
      allocation
        ? {
            mainTeacherId: allocation.main_teacher.active
              ? allocation.main_teacher.id
              : "",
            assistantTeacherIds: allocation.assistant_teachers.map((teacher) => teacher.id),
            weeklyLessonsMin: allocation.weekly_lessons_min,
            weeklyLessonsMax: allocation.weekly_lessons_max
          }
        : {
            mainTeacherId: "",
            assistantTeacherIds: [],
            weeklyLessonsMin: Math.max(1, requirement.missing_lessons),
            weeklyLessonsMax: Math.max(1, requirement.missing_lessons)
          }
    );
  }

  function submitAllocation(): void {
    if (!allocationEditing) return;
    const create = allocationEditing.allocation === null;
    write.mutate({
      path: create
        ? `/projects/${projectId}/planning/allocations`
        : `/projects/${projectId}/planning/allocations/${allocationEditing.allocation?.id}`,
      method: create ? "POST" : "PATCH",
      body: {
        ...(create
          ? { requirement_id: allocationEditing.requirement.id }
          : {}),
        main_teacher_id: allocationForm.mainTeacherId,
        assistant_teacher_ids: allocationForm.assistantTeacherIds,
        weekly_lessons_min: allocationForm.weeklyLessonsMin,
        weekly_lessons_max: allocationForm.weeklyLessonsMax,
        expected_condition_revision: revision
      },
      successMessage: create ? "教师分配已添加" : "教师分配已更新"
    });
  }

  function openTeacherExpansion(): void {
    write.reset();
    setTeacherForm({
      ...emptyTeacherForm,
      gradeCodes: gradeOptions[0] ? [String(gradeOptions[0].code)] : []
    });
    setTeacherOpen(true);
  }

  function submitTeacherExpansion(): void {
    const scope =
      teacherForm.scope === "GRADE"
        ? {
            grade_codes: teacherForm.gradeCodes.map(Number),
            class_ids: []
          }
        : { grade_codes: [], class_ids: teacherForm.classIds };
    write.mutate({
      path: `/projects/${projectId}/planning/teacher-expansions`,
      method: "POST",
      body: {
        teacher_id: teacherForm.mainTeacherId,
        assistant_teacher_ids: teacherForm.assistantTeacherIds,
        course_ids: teacherForm.courseIds,
        role: "MAIN",
        ...scope,
        ...(teacherForm.weeklyLessonsMin &&
        teacherForm.weeklyLessonsMax
          ? {
              weekly_lessons_min: Number(teacherForm.weeklyLessonsMin),
              weekly_lessons_max: Number(teacherForm.weeklyLessonsMax)
            }
          : {}),
        expected_condition_revision: revision
      },
      successMessage: "主教与助教任课范围已应用"
    });
  }

  function submitRoomBatch(): void {
    write.mutate({
      path: `/projects/${projectId}/planning/requirements/room-batch`,
      method: "PATCH",
      body: {
        requirement_ids: roomBatchForm.requirementIds,
        room_type_id: roomBatchForm.roomTypeId || null,
        expected_condition_revision: revision
      },
      successMessage: "所选课程的功能教室安排已更新"
    });
  }

  function confirmDelete(): void {
    if (!deleteTarget) return;
    const isRequirement = deleteTarget.kind === "requirement";
    const id = isRequirement
      ? deleteTarget.requirement.id
      : deleteTarget.allocation.id;
    write.mutate({
      path: `/projects/${projectId}/planning/${
        isRequirement ? "requirements" : "allocations"
      }/${id}?expected_condition_revision=${revision}`,
      method: "DELETE",
      successMessage: isRequirement ? "班级课程需求已删除" : "教师分配已删除"
    });
  }

  function focusIssue(requirementId: string | null): void {
    if (!requirementId) return;
    setFocusRequirementId(requirementId);
    setStep(2);
  }

  const allocationCount = requirements.reduce(
    (total, item) => total + item.allocations.length,
    0
  );
  const incompleteCount = requirements.filter(
    (item) => item.missing_lessons !== 0 || item.allocations.length === 0
  ).length;
  const quickClassCount = classItems.filter(
    (item) => quickGrades.map(Number).includes(item.grade_code)
  ).length;
  const quickCourseCount = quickRules.reduce(
    (total, rule) => total + rule.courseIds.length,
    0
  );
  const teacherImpact = requirements.filter((item) => {
    if (!teacherForm.courseIds.includes(item.course.id)) return false;
    return teacherForm.scope === "GRADE"
      ? teacherForm.gradeCodes.map(Number).includes(item.school_class.grade_code)
      : teacherForm.classIds.includes(item.school_class.id);
  }).length;
  const roomOverview = roomItems.map((room) => {
    const matched = requirements.filter((item) => item.room_type?.id === room.id);
    return {
      ...room,
      requirementCount: matched.length,
      lessonCount: matched.reduce((total, item) => total + item.weekly_lessons, 0)
    };
  });
  const teacherWorkloadSources: TeacherWorkloadSource[] = requirements.flatMap(
    (requirement) =>
      requirement.allocations.map((allocation) => ({
        mainTeacherId: allocation.main_teacher.id,
        assistantTeacherIds: allocation.assistant_teachers.map(
          (teacher) => teacher.id
        ),
        minimum: allocation.weekly_lessons_min,
        maximum: allocation.weekly_lessons_max,
        weekPattern: requirement.week_pattern
      }))
  );
  const teacherWorkloadLabels = buildTeacherWorkloadLabels(
    teacherItems,
    teacherWorkloadSources
  );
  const teacherOptions = teacherItems.map((teacher) => ({
    ...teacher,
    workloadLabel: teacherWorkloadLabels.get(teacher.id)
  }));

  return (
    <main className="page-shell page-shell-wide">
      <ContextHeader
        projectId={projectId}
        eyebrow="任课计划 · 阶段 1/2"
        title="编制基础规则"
        backTo={{ href: `/projects/${projectId}`, label: "返回课表主页" }}
        actions={
          <div className="header-actions">
            <button
              className="secondary-button danger-text"
              type="button"
              disabled={planDemo.clear.pending || planDemo.mutation.isPending}
              onClick={planDemo.clear.start}
            >
              {planDemo.clear.previewMutation.isPending
                ? "正在统计…"
                : "一键清除模拟数据"}
            </button>
            <button
              className="plan-demo-button"
              type="button"
              disabled={loading || planDemo.mutation.isPending || planDemo.clear.pending}
              onClick={planDemo.start}
            >
              {planDemo.mutation.isPending ? "正在填入…" : "使用模拟数据"}
            </button>
          </div>
        }
      />
      {notice ? (
        <p className="success-notice" role="status" aria-live="polite">
          {notice}
        </p>
      ) : null}
      {loading ? <p className="state-message">正在加载基础规则…</p> : null}
      {loadError ? <ErrorText error={loadError} /> : null}
      {planDemo.visibleError ? <ErrorText error={planDemo.visibleError} /> : null}
      {planDemo.clear.visibleError ? <ErrorText error={planDemo.clear.visibleError} /> : null}
      {!loading && (classItems.length === 0 || courseItems.length === 0) ? (
        <section className="warning-banner">
          <h2>先补齐基础信息</h2>
          <p>
            {classItems.length === 0 ? "还没有班级。" : ""}
            {courseItems.length === 0 ? "还没有课程。" : ""}
            请先前往
            {classItems.length === 0 ? (
              <a className="text-link" href={`/projects/${projectId}/classes`}>
                班级信息
              </a>
            ) : null}
            {classItems.length === 0 && courseItems.length === 0 ? " 和 " : null}
            {courseItems.length === 0 ? (
              <a className="text-link" href={`/projects/${projectId}/courses`}>
                课程信息
              </a>
            ) : null}
            。
          </p>
        </section>
      ) : null}

      {!loading && step !== null ? (
        <>
          <nav className="steps-bar" aria-label="编制步骤">
            <button
              type="button"
              className={step === 1 ? "active" : ""}
              aria-current={step === 1 ? "step" : undefined}
              onClick={() => setStep(1)}
            >
              1 班级课程需求
            </button>
            <button
              type="button"
              className={step === 2 ? "active" : ""}
              aria-current={step === 2 ? "step" : undefined}
              onClick={() => setStep(2)}
            >
              2 教师任课分配
            </button>
            <button
              type="button"
              className={step === 3 ? "active" : ""}
              aria-current={step === 3 ? "step" : undefined}
              onClick={() => setStep(3)}
            >
              3 检查并生成
            </button>
          </nav>
          <div className="plan-layout">
            <div className="plan-workspace">
              {step === 1 ? (
      <section aria-label="第 1 步：班级课程需求">
        <div className="section-heading planning-step-heading">
          <div>
            <h2>班级课程需求</h2>
          </div>
          <div className="header-actions">
            <button
              className="ghost-button"
              type="button"
              disabled={loading || classItems.length === 0 || courseItems.length === 0}
              onClick={() => openRequirement("new")}
            >
              批量添加课程需求
            </button>
            <button
              className="solid-button"
              type="button"
              disabled={loading || gradeOptions.length === 0 || courseItems.length === 0}
              onClick={openQuick}
            >
              按年级批量填写
            </button>
          </div>
        </div>
        <PlanningImportActions
          projectId={projectId}
          conditionRevision={revision}
          locked={draft.data?.locked ?? false}
          catalog={{
            classes: classItems,
            courses: courseItems,
            teachers: teacherItems,
            rooms: roomItems,
            slots: slots.data?.items ?? []
          }}
        />
        <CourseTimeRulesPanel
          projectId={projectId}
          conditionRevision={revision}
          classCoursePairs={requirements.map((item) => ({
            classId: item.school_class.id,
            courseId: item.course.id
          }))}
        />
        <PlanningFilters
          label="筛选班级课程需求"
          gradeOptions={gradeOptions}
          classes={visibleClasses}
          teachers={teacherItems}
          courses={courseItems}
          selectedGrade={selectedGrade}
          selectedClass={selectedClass}
          selectedTeacher={selectedTeacher}
          selectedCourse={selectedCourse}
          resultCount={visibleRequirements.length}
          onGradeChange={(value) => {
            setSelectedGrade(value);
            setSelectedClass("ALL");
          }}
          onClassChange={setSelectedClass}
          onTeacherChange={setSelectedTeacher}
          onCourseChange={setSelectedCourse}
        />
        <section className="content-card planning-table-card">
          {draft.data && requirements.length === 0 ? (
            <div className="empty-card compact">
              <h2>还没有班级课程需求</h2>
            </div>
          ) : null}
          {requirements.length > 0 && visibleRequirements.length === 0 ? (
            <p className="state-message">当前筛选下没有课程需求。</p>
          ) : null}
          {visibleRequirements.length > 0 ? (
            <table className="data-table">
              <thead>
                <tr>
                  <th>班级 / 课程</th>
                  <th>周课时</th>
                  <th>上课周次</th>
                  <th>规则</th>
                  <th>功能教室</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {visibleRequirements.map((item) => (
                  <tr
                    key={item.id}
                    className={`requirement-row ${item.invalid_fields.length > 0 ? "planning-row-invalid" : ""}`}
                  >
                    <td>
                      <strong>{item.school_class.label}</strong>{" "}
                      <span className={item.invalid_fields.includes("course_id") ? "invalid-reference" : ""}>{item.course.label}</span>
                      {item.invalid_fields.length > 0 ? <small className="invalid-reference">引用对象已删除，请重新选择</small> : null}
                    </td>
                    <td>{item.weekly_lessons} 节</td>
                    <td>{weekPatternLabel(item.week_pattern)}</td>
                    <td>
                      {item.consecutive ? "连课" : "不连课"}
                      {item.allow_multiple_groups_per_day ? " · 同日可多节" : ""}
                      <span>可排节次：{availableTimeSummary(item.time_preference, item.available_time_slots, slots.data?.items ?? [], item.available_time_cells)}</span>
                      {item.non_adjacent_course_hints.length > 0 ? (
                        <span>与{item.non_adjacent_course_hints.map((course) => course.label).join("、")}不相邻</span>
                      ) : null}
                    </td>
                    <td>{item.room_type ? `${item.room_type.label}（${item.room_type.count} 间）` : "无"}</td>
                    <td>
                      <div className="row-actions">
                        <button className="link-button" type="button" onClick={() => openRequirement(item)}>编辑</button>
                        <button className="link-button danger-text" type="button" onClick={() => {
                          write.reset();
                          setDeleteTarget({ kind: "requirement", requirement: item });
                        }}>删除</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </section>
      </section>
              ) : null}
              {step === 2 ? (
      <section aria-label="第 2 步：教师任课分配">
        <div className="section-heading planning-step-heading">
          <div>
            <h2>教师任课分配</h2>
          </div>
          <div className="header-actions">
            <button
              className="ghost-button"
              type="button"
              disabled={loading || classItems.length === 0 || courseItems.length === 0}
              onClick={() => openRequirement("new")}
            >
              批量添加课程需求
            </button>
            <button
              className="ghost-button"
              type="button"
              disabled={requirements.length === 0}
              onClick={() => {
                write.reset();
                setRoomBatchForm({ requirementIds: [], roomTypeId: "" });
                setRoomBatchOpen(true);
              }}
            >
              批量设置功能教室
            </button>
            <button
              className="solid-button"
              type="button"
              disabled={requirements.length === 0 || teacherItems.length === 0}
              onClick={openTeacherExpansion}
            >
              按教师范围分配
            </button>
          </div>
        </div>
        {teacherItems.length === 0 ? (
          <section className="empty-card compact planning-inline-empty">
            <h2>还没有教师</h2>
            <p>
              请先从<a className="text-link" href={`/projects/${projectId}/teachers`}>教师信息</a>
              中同步本学期教师。
            </p>
          </section>
        ) : (
          <div className="planning-summary-grid">
            <div><strong>{requirements.length}</strong><span>班级课程需求</span></div>
            <div><strong>{allocationCount}</strong><span>教师分配</span></div>
            <button
              className={`planning-summary-action ${incompleteFirst ? "active" : ""}`}
              type="button"
              aria-pressed={incompleteFirst}
              onClick={pinIncompleteRequirements}
            >
              <strong>{incompleteCount}</strong><span>待补齐需求</span>
              <small>{incompleteFirst ? "再次点击刷新" : "点击置顶"}</small>
            </button>
          </div>
        )}
        {requirements.length > 0 ? (
          <PlanningFilters
            label="筛选教师任课分配"
            gradeOptions={gradeOptions}
            classes={visibleClasses}
            teachers={teacherItems}
            courses={courseItems}
            selectedGrade={selectedGrade}
            selectedClass={selectedClass}
            selectedTeacher={selectedTeacher}
            selectedCourse={selectedCourse}
            resultCount={visibleRequirements.length}
            onGradeChange={(value) => {
              setSelectedGrade(value);
              setSelectedClass("ALL");
            }}
            onClassChange={setSelectedClass}
            onTeacherChange={setSelectedTeacher}
            onCourseChange={setSelectedCourse}
          />
        ) : null}
        <section className="content-card planning-table-card">
          {requirements.length === 0 ? (
            <div className="empty-card compact">
              <h2>还没有班级课程需求</h2>
              <p>请先回到第 1 步，用“按年级批量填写”或“批量添加课程需求”创建课程需求。</p>
            </div>
          ) : null}
          {requirements.length > 0 && visibleRequirements.length === 0 ? (
            <p className="state-message">当前筛选下没有教师分配。</p>
          ) : null}
          {visibleRequirements.length > 0 ? (
            <table className="data-table planning-table">
              <thead>
                <tr>
                  <th>班级 / 课程</th>
                  <th>课时分配</th>
                  <th>主教</th>
                  <th>规则</th>
                  <th>功能教室</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {orderedVisibleRequirements.map((item) => {
                  const invalid = item.invalid_fields.length > 0;
                  const incomplete = item.missing_lessons !== 0 || item.allocations.length === 0;
                  return (
                    <RequirementRows
                      key={item.id}
                      item={item}
                      slots={slots.data?.items ?? []}
                      invalid={invalid}
                      incomplete={incomplete}
                      onEdit={() => openRequirement(item)}
                      onDelete={() => {
                        write.reset();
                        setDeleteTarget({ kind: "requirement", requirement: item });
                      }}
                      onAddAllocation={() => openAllocation(item, null)}
                      onOpenAllocationEditor={() =>
                        openAllocation(item, item.allocations[0] ?? null)
                      }
                      onEditAllocation={(allocation) => openAllocation(item, allocation)}
                      onDeleteAllocation={(allocation) => {
                        write.reset();
                        setDeleteTarget({
                          kind: "allocation",
                          requirement: item,
                          allocation
                        });
                      }}
                    />
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </section>
      </section>
              ) : null}
              {step === 3 ? (

      <section aria-label="第 3 步：检查并生成">
        <div className="section-heading planning-step-heading">
          <div>
            <h2>检查并生成</h2>
          </div>
          <div className="header-actions">
            {shouldEmphasizeRuleCheck ? (
              <span className="next-action-prompt">下一步 →</span>
            ) : null}
            <button
              className={shouldEmphasizeRuleCheck ? "solid-button next-action-button" : "ghost-button"}
              type="button"
              disabled={!draft.data || preview.isPending}
              onClick={() => preview.mutate()}
            >
              {preview.isPending ? "正在检查…" : "检查完整规则"}
            </button>
            <button
              className="solid-button"
              type="button"
              disabled={
                !previewResult?.ready ||
                previewResult.condition_revision !== revision ||
                compile.isPending
              }
              onClick={() => compile.mutate()}
            >
              {compile.isPending ? "正在生成…" : "生成详细任课计划"}
            </button>
          </div>
        </div>
        <div className="room-overview" aria-label="功能教室安排总览">
          <article>
            <h3>无指定功能教室</h3>
            <strong>{requirements.filter((item) => item.room_type === null).length} 项</strong>
          </article>
          {roomOverview.map((room) => (
            <article key={room.id}>
              <h3>{room.name}（{room.count} 间）</h3>
              <strong>{room.requirementCount} 项 · {room.lessonCount} 节</strong>
              <span>生成时自动均衡到具体功能教室</span>
            </article>
          ))}
        </div>
        {previewResult ? (
          <section
            className={`preview-panel ${previewResult.ready ? "ready" : "invalid"}`}
            aria-live="polite"
          >
            <div className="preview-heading">
              <div>
                <h3>{previewResult.ready ? "规则检查通过" : "请完善以下规则"}</h3>
                <p>
                  将生成 {previewResult.plan_count} 条详细计划，共 {previewResult.total_lessons} 节周课时。
                </p>
              </div>
              <span className={`status-pill ${previewResult.ready ? "" : "invalid"}`}>
                {previewResult.ready ? "可以生成" : `${previewResult.issues.length} 个问题`}
              </span>
            </div>
            {previewResult.issues.length > 0 ? (
              <div className="issue-list">
                {previewResult.issues.map((issue, index) => (
                  <div className="issue" key={`${issue.code}-${issue.requirement_id}-${index}`}>
                    <div>
                      <strong>{issue.message}</strong>
                      <small>{issue.code}</small>
                    </div>
                    {issue.requirement_id ? (
                      <button
                        className="link-button"
                        type="button"
                        onClick={() => focusIssue(issue.requirement_id)}
                      >
                        去完善
                      </button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
            {previewResult.room_loads.length > 0 ? (
              <div className="preview-room-loads">
                <h4>具体功能教室分配预览</h4>
                <ul>
                  {previewResult.room_loads.map((load) => {
                    const room = roomItems.find((item) => item.id === load.room_type_id);
                    const instance = room?.instances.find((item) => item.id === load.room_instance_id);
                    return (
                      <li key={load.room_instance_id}>
                        <span>{instance?.display_name ?? `${room?.name ?? "功能教室"}${load.ordinal}`}</span>
                        <strong>{load.weekly_lessons} 节</strong>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
          </section>
        ) : (
          <section className="empty-card compact planning-inline-empty">
            <h2>尚未检查</h2>
            <p>检查不会修改数据。</p>
          </section>
        )}
        <ErrorText error={preview.error ?? compile.error} />
      </section>
              ) : null}
            </div>
            <aside className="plan-aside">
              {step === 1 ? (
                <>
                  <h3>第 1 步 · 班级课程需求</h3>
                  <div className="fact">
                    <strong>{requirements.length}</strong>
                    <span>项课程需求</span>
                  </div>
                  <div className="fact">
                    <strong>{visibleRequirements.length}</strong>
                    <span>当前筛选结果</span>
                  </div>
                  <button
                    className="ghost-button aside-next"
                    type="button"
                    onClick={() => setStep(2)}
                  >
                    继续教师分配 →
                  </button>
                </>
              ) : null}
              {step === 2 ? (
                <>
                  <h3>第 2 步 · 教师任课分配</h3>
                  <button
                    className={`fact fact-button ${incompleteFirst ? "active" : ""}`}
                    type="button"
                    aria-pressed={incompleteFirst}
                    onClick={pinIncompleteRequirements}
                  >
                    <strong>{incompleteCount}</strong>
                    <span>待补齐需求</span>
                    <small>{incompleteFirst ? "再次点击刷新" : "点击置顶"}</small>
                  </button>
                  <div className="fact">
                    <strong>{allocationCount}</strong>
                    <span>条教师分配</span>
                  </div>
                  <button
                    className="ghost-button aside-next"
                    type="button"
                    onClick={() => setStep(3)}
                  >
                    前往检查并生成 →
                  </button>
                </>
              ) : null}
              {step === 3 ? (
                <>
                  <h3>第 3 步 · 检查并生成</h3>
                  <div className="fact">
                    <strong>{previewResult ? previewResult.issues.length : incompleteCount}</strong>
                    <span>待完善问题</span>
                  </div>
                  <div className="fact">
                    <strong>{previewResult?.ready ? previewResult.plan_count : "—"}</strong>
                    <span>将生成的详细计划</span>
                  </div>
                  <button
                    className="ghost-button aside-next"
                    type="button"
                    onClick={() => setStep(2)}
                  >
                    ← 返回教师分配
                  </button>
                </>
              ) : null}
              <TeacherWorkloadPanel
                teachers={teacherItems}
                sources={teacherWorkloadSources}
                selectedTeacherId={
                  selectedTeacher === "ALL" ? undefined : selectedTeacher
                }
              />
            </aside>
          </div>
        </>
      ) : null}

      {quickOpen ? (
        <EditorModal
          wide
          title="按年级批量填写"
          submitLabel="批量创建课程需求"
          onClose={() => setQuickOpen(false)}
          onSubmit={submitQuick}
          pending={write.isPending}
          submitDisabled={
            quickGrades.length === 0 ||
            quickRules.some(
              (rule) =>
                rule.courseIds.length === 0 ||
                !isAvailableTimeSelectionValid(
                  rule.timePreference,
                  rule.availableTimeCells,
                  slots.data?.items ?? []
                )
            )
          }
          error={write.error}
        >
          <CheckboxMultiSelect
            legend="年级"
            value={quickGrades}
            options={gradeOptions.map((grade) => ({
              id: String(grade.code),
              label: grade.label
            }))}
            onChange={setQuickGrades}
          />
          <p className="impact-summary">
            将为所选 {quickGrades.length} 个年级、{quickClassCount} 个班创建最多 {quickClassCount * quickCourseCount} 项课程需求；已有同班同课程会跳过，不会覆盖。
          </p>
          <div className="quick-rule-list">
            {quickRules.map((rule, index) => (
              <fieldset className="quick-rule" key={rule.key}>
                <legend>课程需求 {index + 1}</legend>
                <div className="plan-form-grid">
                  <CheckboxMultiSelect
                    legend="课程"
                    value={rule.courseIds}
                    options={courseItems.map((item) => ({
                      id: item.id,
                      label: item.name
                    }))}
                    onChange={(courseIds) =>
                      setQuickRules((items) =>
                        items.map((item) =>
                          item.key === rule.key ? { ...item, courseIds } : item
                        )
                      )
                    }
                  />
                  <LessonCountField
                    value={rule.weeklyLessons}
                    onChange={(weeklyLessons) =>
                      setQuickRules((items) => items.map((item) =>
                        item.key === rule.key
                          ? {
                              ...item,
                              weeklyLessons,
                              consecutive: weeklyLessons === 1 ? false : item.consecutive
                            }
                          : item
                      ))
                    }
                  />
                  <WeekPatternField
                    value={rule.weekPattern}
                    onChange={(weekPattern) =>
                      setQuickRules((items) =>
                        items.map((item) =>
                          item.key === rule.key ? { ...item, weekPattern } : item
                        )
                      )
                    }
                  />
                  <RoomField
                    value={rule.roomTypeId}
                    rooms={roomItems}
                    onChange={(roomTypeId) =>
                      setQuickRules((items) => items.map((item) => item.key === rule.key ? { ...item, roomTypeId } : item))
                    }
                  />
                </div>
                <AvailableTimeSlotsField
                  slots={slots.data?.items ?? []}
                  timePreference={rule.timePreference}
                  value={rule.availableTimeCells}
                  onChange={(timePreference, availableTimeCells) =>
                    setQuickRules((items) =>
                      items.map((item) =>
                        item.key === rule.key
                          ? { ...item, timePreference, availableTimeCells }
                          : item
                      )
                    )
                  }
                />
                <RuleChecks
                  weeklyLessons={rule.weeklyLessons}
                  consecutive={rule.consecutive}
                  allowMultiple={rule.allowMultiple}
                  onConsecutive={(consecutive) => setQuickRules((items) => items.map((item) => item.key === rule.key ? { ...item, consecutive } : item))}
                  onAllowMultiple={(allowMultiple) => setQuickRules((items) => items.map((item) => item.key === rule.key ? { ...item, allowMultiple } : item))}
                />
                {quickRules.length > 1 ? (
                  <button className="text-button danger" type="button" onClick={() => setQuickRules((items) => items.filter((item) => item.key !== rule.key))}>
                    移除这组条件
                  </button>
                ) : null}
              </fieldset>
            ))}
          </div>
          <button className="secondary-button add-rule-button" type="button" onClick={() => setQuickRules((items) => [...items, emptyQuickRule()])}>
            ＋ 添加一组不同条件
          </button>
        </EditorModal>
      ) : null}

      {requirementEditing ? (
        <EditorModal
          wide
          title={requirementEditing === "new" ? "批量添加课程需求" : "编辑班级课程需求"}
          onClose={() => {
            setRequirementDialogError(null);
            setRequirementEditing(null);
          }}
          onSubmit={submitRequirement}
          pending={write.isPending}
          submitDisabled={
            (requirementEditing === "new" &&
              (requirementForm.classIds.length === 0 ||
                requirementForm.courseIds.length === 0)) ||
            !isAvailableTimeSelectionValid(
              requirementForm.timePreference,
              requirementForm.availableTimeCells,
              slots.data?.items ?? []
            )
          }
          error={write.error}
        >
          <div className="plan-form-grid">
            {requirementEditing === "new" ? (
              <>
                <CheckboxMultiSelect
                  legend="班级"
                  value={requirementForm.classIds}
                  options={classItems.map((item) => ({
                    id: item.id,
                    label: `${item.grade_label}${item.class_name}`
                  }))}
                  onChange={(classIds) =>
                    setRequirementForm({ ...requirementForm, classIds })
                  }
                />
                <CheckboxMultiSelect
                  legend="课程"
                  value={requirementForm.courseIds}
                  options={courseItems.map((item) => ({
                    id: item.id,
                    label: item.name
                  }))}
                  onChange={(courseIds) =>
                    setRequirementForm({ ...requirementForm, courseIds })
                  }
                />
              </>
            ) : (
              <>
                <label className="field"><span>班级</span><select required value={requirementForm.classId} onChange={(event) => setRequirementForm({ ...requirementForm, classId: event.target.value })}><option value="">请选择</option>{classItems.map((item) => <option key={item.id} value={item.id}>{item.grade_label}{item.class_name}</option>)}</select></label>
                <label className="field"><span>课程</span><select required value={requirementForm.courseId} onChange={(event) => setRequirementForm({ ...requirementForm, courseId: event.target.value })}><option value="">请选择</option>{courseItems.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
              </>
            )}
            <LessonCountField value={requirementForm.weeklyLessons} onChange={(weeklyLessons) => setRequirementForm({ ...requirementForm, weeklyLessons, consecutive: weeklyLessons === 1 ? false : requirementForm.consecutive })} />
            <WeekPatternField value={requirementForm.weekPattern} onChange={(weekPattern) => setRequirementForm({ ...requirementForm, weekPattern })} />
            <RoomField value={requirementForm.roomTypeId} rooms={roomItems} onChange={(roomTypeId) => setRequirementForm({ ...requirementForm, roomTypeId })} />
          </div>
          {requirementEditing === "new" ? (
            <p className="impact-summary">
              将创建 {requirementForm.classIds.length * requirementForm.courseIds.length} 个班级×课程组合；重复项会跳过，其他项正常保存。
            </p>
          ) : null}
          <AvailableTimeSlotsField
            slots={slots.data?.items ?? []}
            timePreference={requirementForm.timePreference}
            value={requirementForm.availableTimeCells}
            currentReferences={
              requirementEditing === "new"
                ? []
                : requirementEditing.available_time_cells ?? []
            }
            onChange={(timePreference, availableTimeCells) =>
              setRequirementForm({ ...requirementForm, timePreference, availableTimeCells })
            }
          />
          <RuleChecks weeklyLessons={requirementForm.weeklyLessons} consecutive={requirementForm.consecutive} allowMultiple={requirementForm.allowMultiple} onConsecutive={(consecutive) => setRequirementForm({ ...requirementForm, consecutive })} onAllowMultiple={(allowMultiple) => setRequirementForm({ ...requirementForm, allowMultiple })} />
        </EditorModal>
      ) : null}

      {requirementDialogError ? (
        <ErrorDialog
          error={requirementDialogError}
          onClose={() => setRequirementDialogError(null)}
        />
      ) : null}

      {allocationEditing ? (
        <EditorModal
          wide
          className="teacher-allocation-modal"
          title={allocationEditing.allocation ? "编辑教师分配" : "添加教师分配"}
          onClose={() => setAllocationEditing(null)}
          onSubmit={submitAllocation}
          pending={write.isPending}
          submitDisabled={!allocationForm.mainTeacherId}
          error={write.error}
        >
          <p className="impact-summary">{allocationEditing.requirement.school_class.label} · {allocationEditing.requirement.course.label}，{weekPatternLabel(allocationEditing.requirement.week_pattern)} {allocationEditing.requirement.weekly_lessons} 节。</p>
          <TeacherSearchSelect
            label="主教老师"
            value={allocationForm.mainTeacherId}
            options={teacherOptions}
            onChange={(mainTeacherId) => setAllocationForm({
              ...allocationForm,
              mainTeacherId,
              assistantTeacherIds: allocationForm.assistantTeacherIds.filter((id) => id !== mainTeacherId)
            })}
          />
          <AssistantMultiSelect value={allocationForm.assistantTeacherIds} options={teacherOptions} disabledIds={allocationForm.mainTeacherId ? [allocationForm.mainTeacherId] : []} onChange={(assistantTeacherIds) => setAllocationForm({ ...allocationForm, assistantTeacherIds })} />
          <LessonRangeField
            minimum={allocationForm.weeklyLessonsMin}
            maximum={allocationForm.weeklyLessonsMax}
            onChange={(weeklyLessonsMin, weeklyLessonsMax) =>
              setAllocationForm({
                ...allocationForm,
                weeklyLessonsMin,
                weeklyLessonsMax
              })
            }
          />
        </EditorModal>
      ) : null}

      {teacherOpen ? (
        <EditorModal
          wide
          title="按教师任课范围分配"
          submitLabel="应用任课范围"
          onClose={() => setTeacherOpen(false)}
          onSubmit={submitTeacherExpansion}
          pending={write.isPending}
          submitDisabled={
            !teacherForm.mainTeacherId ||
            teacherForm.courseIds.length === 0 ||
            (teacherForm.scope === "GRADE"
              ? teacherForm.gradeCodes.length === 0
              : teacherForm.classIds.length === 0)
          }
          error={write.error}
        >
          <div className="plan-form-grid">
            <TeacherSearchSelect
              label="主教老师"
              value={teacherForm.mainTeacherId}
              options={teacherOptions}
              onChange={(mainTeacherId) => setTeacherForm({
                ...teacherForm,
                mainTeacherId,
                assistantTeacherIds: teacherForm.assistantTeacherIds.filter((id) => id !== mainTeacherId)
              })}
            />
            <AssistantMultiSelect
              value={teacherForm.assistantTeacherIds}
              options={teacherOptions}
              disabledIds={teacherForm.mainTeacherId ? [teacherForm.mainTeacherId] : []}
              onChange={(assistantTeacherIds) => setTeacherForm({ ...teacherForm, assistantTeacherIds })}
            />
            <CheckboxMultiSelect
              legend="课程"
              value={teacherForm.courseIds}
              options={courseItems.map((item) => ({ id: item.id, label: item.name }))}
              onChange={(courseIds) => setTeacherForm({ ...teacherForm, courseIds })}
            />
            <label className="field"><span>范围方式</span><select value={teacherForm.scope} onChange={(event) => setTeacherForm({ ...teacherForm, scope: event.target.value as "GRADE" | "CLASSES" })}><option value="GRADE">整年级</option><option value="CLASSES">指定班级</option></select></label>
          </div>
          {teacherForm.scope === "GRADE" ? (
            <CheckboxMultiSelect
              legend="年级"
              value={teacherForm.gradeCodes}
              options={gradeOptions.map((grade) => ({
                id: String(grade.code),
                label: grade.label
              }))}
              onChange={(gradeCodes) => setTeacherForm({ ...teacherForm, gradeCodes })}
            />
          ) : (
            <CheckboxMultiSelect
              legend="班级"
              value={teacherForm.classIds}
              options={classItems.map((item) => ({
                id: item.id,
                label: `${item.grade_label}${item.class_name}`
              }))}
              onChange={(classIds) => setTeacherForm({ ...teacherForm, classIds })}
            />
          )}
          <fieldset className="lesson-range-field optional-range">
              <legend>统一课时范围（可选）</legend>
              <div className="lesson-range-inputs">
                <label>
                  <span className="visually-hidden">最少课时</span>
                  <input
                    type="number"
                    min="1"
                    max={teacherForm.weeklyLessonsMax || "15"}
                    required={Boolean(teacherForm.weeklyLessonsMax)}
                    placeholder="最少"
                    value={teacherForm.weeklyLessonsMin}
                    onChange={(event) => setTeacherForm({ ...teacherForm, weeklyLessonsMin: event.target.value })}
                  />
                </label>
                <span>至</span>
                <label>
                  <span className="visually-hidden">最多课时</span>
                  <input
                    type="number"
                    min={teacherForm.weeklyLessonsMin || "1"}
                    max="15"
                    required={Boolean(teacherForm.weeklyLessonsMin)}
                    placeholder="最多"
                    value={teacherForm.weeklyLessonsMax}
                    onChange={(event) => setTeacherForm({ ...teacherForm, weeklyLessonsMax: event.target.value })}
                  />
                </label>
              </div>
              <small className="field-hint">两项都留空时，首次分配承担该班课程的全部需求。</small>
          </fieldset>
          <p className="impact-summary">当前范围匹配 {teacherImpact} 项班级课程需求；所选助教将与主教一起添加到同一条分配中。</p>
        </EditorModal>
      ) : null}

      {roomBatchOpen ? (
        <EditorModal
          wide
          title="批量设置课程功能教室"
          submitLabel="应用功能教室安排"
          onClose={() => setRoomBatchOpen(false)}
          onSubmit={submitRoomBatch}
          pending={write.isPending}
          error={write.error}
        >
          <RoomField value={roomBatchForm.roomTypeId} rooms={roomItems} onChange={(roomTypeId) => setRoomBatchForm({ ...roomBatchForm, roomTypeId })} />
          <fieldset className="scope-picker"><legend>选择班级课程需求</legend>{requirements.map((item) => <label className="check-row" key={item.id}><input type="checkbox" checked={roomBatchForm.requirementIds.includes(item.id)} onChange={(event) => setRoomBatchForm({ ...roomBatchForm, requirementIds: event.target.checked ? [...roomBatchForm.requirementIds, item.id] : roomBatchForm.requirementIds.filter((id) => id !== item.id) })} /><span>{item.school_class.label} · {item.course.label}</span></label>)}</fieldset>
        </EditorModal>
      ) : null}

      {deleteTarget ? (
        <ConfirmModal
          title={deleteTarget.kind === "requirement" ? "删除班级课程需求" : "删除教师分配"}
          description={deleteTarget.kind === "requirement" ? `确认删除“${deleteTarget.requirement.school_class.label} · ${deleteTarget.requirement.course.label}”及其全部教师分配？` : `确认删除“${deleteTarget.allocation.main_teacher.label}”在“${deleteTarget.requirement.school_class.label} · ${deleteTarget.requirement.course.label}”中的分配？`}
          dangerLabel="确认删除"
          onClose={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
          pending={write.isPending}
          error={write.error}
        />
      ) : null}
      {planDemo.confirmOpen ? (
        <ConfirmModal
          title="使用模拟数据并重新编制？"
          description="这会清空现有任课计划和可用节次设置，并替换为一套完整模拟规则；课程不相邻设置、基础资料和现有课表都会保留。"
          dangerLabel="确认使用模拟数据"
          onClose={planDemo.closeConfirm}
          onConfirm={planDemo.confirm}
          pending={planDemo.mutation.isPending}
          error={planDemo.confirmError}
        />
      ) : null}
      {planDemo.clear.preview ? (
        <ConfirmModal
          title="清除任课计划模拟数据？"
          description={`${demoClearSummary(planDemo.clear.preview)}。只会清理来源明确、从未人工改动且未被当前课表或其他数据引用的任课计划模拟记录；现有课表和全部历史版本保持不变。`}
          dangerLabel="确认清理"
          onClose={planDemo.clear.close}
          onConfirm={planDemo.clear.confirm}
          pending={planDemo.clear.clearMutation.isPending}
          error={planDemo.clear.clearMutation.error}
        />
      ) : null}
    </main>
  );
}

function RequirementRows({
  item,
  slots,
  invalid,
  incomplete,
  onEdit,
  onDelete,
  onAddAllocation,
  onOpenAllocationEditor,
  onEditAllocation,
  onDeleteAllocation
}: {
  item: Requirement;
  slots: AvailableSlotOption[];
  invalid: boolean;
  incomplete: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onAddAllocation: () => void;
  onOpenAllocationEditor: () => void;
  onEditAllocation: (allocation: Allocation) => void;
  onDeleteAllocation: (allocation: Allocation) => void;
}): React.JSX.Element {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const detailsId = `teacher-allocations-${item.id}`;

  function enterAllocationEditor(): void {
    setDetailsOpen(true);
    requestAnimationFrame(() => {
      detailsRef.current?.scrollIntoView({ block: "nearest" });
    });
    onOpenAllocationEditor();
  }

  return (
    <>
      <tr
        id={`requirement-${item.id}`}
        className={`requirement-row ${invalid || incomplete ? "planning-row-invalid" : ""}`}
        tabIndex={-1}
      >
        <td>
          <strong>{item.school_class.label}</strong>
          <span className={item.invalid_fields.includes("course_id") ? "invalid-reference" : ""}>{item.course.label}</span>
          {item.invalid_fields.length > 0 ? <small className="invalid-reference">引用对象已删除，请重新选择</small> : null}
        </td>
        <td>
          <strong>{formatLessonRange(item.allocated_lessons_min, item.allocated_lessons_max)} / {item.weekly_lessons} 节</strong>
          <span className={item.missing_lessons === 0 ? "" : "invalid-reference"}>{item.missing_lessons === 0 ? item.allocated_lessons_min === item.allocated_lessons_max ? "已分配完整" : "范围可覆盖需求" : item.missing_lessons > 0 ? `最多还缺 ${item.missing_lessons} 节` : `最少超出 ${Math.abs(item.missing_lessons)} 节`}</span>
        </td>
        <td>
          <div className="allocation-summary-cell">
            {item.allocations.length > 0 ? (
              <span>{item.allocations.map((allocation) => allocation.main_teacher.label).join("、")}</span>
            ) : (
              <span className="invalid-reference">未分配</span>
            )}
            <button
              className="mini-button allocation-entry-button"
              type="button"
              aria-controls={detailsId}
              aria-expanded={detailsOpen}
              onClick={enterAllocationEditor}
            >
              设置主教 / 助教
            </button>
          </div>
        </td>
        <td>{weekPatternLabel(item.week_pattern)} · {item.consecutive ? "连课" : "不连课"}{item.allow_multiple_groups_per_day ? " · 同日可多节" : ""}<span>可排节次：{availableTimeSummary(item.time_preference, item.available_time_slots, slots, item.available_time_cells)}</span>{item.non_adjacent_course_hints.length > 0 ? <span>与{item.non_adjacent_course_hints.map((course) => course.label).join("、")}不相邻</span> : null}</td>
        <td>{item.room_type ? `${item.room_type.label}（${item.room_type.count} 间）` : "无"}</td>
        <td><div className="row-actions"><button className="text-button" type="button" onClick={onEdit}>编辑</button><button className="text-button danger" type="button" onClick={onDelete}>删除</button></div></td>
      </tr>
      <tr className="allocation-detail-row">
        <td colSpan={6}>
          <details
            id={detailsId}
            ref={detailsRef}
            open={detailsOpen}
            onToggle={(event) => setDetailsOpen(event.currentTarget.open)}
          >
            <summary>主教 / 助教设置（{item.allocations.length} 条）</summary>
            <div className="allocation-list">
              {item.allocations.map((allocation) => (
                <div className={allocation.invalid_fields.length > 0 ? "invalid" : ""} key={allocation.id}>
                  <span><strong>{allocation.main_teacher.label}</strong> 主教 · {formatLessonRange(allocation.weekly_lessons_min, allocation.weekly_lessons_max)} 节{allocation.assistant_teachers.length ? ` · ${allocation.assistant_teachers.map((teacher) => teacher.label).join("、")} 助教` : ""}</span>
                  <div className="row-actions"><button className="text-button" type="button" onClick={() => onEditAllocation(allocation)}>编辑</button><button className="text-button danger" type="button" onClick={() => onDeleteAllocation(allocation)}>删除</button></div>
                </div>
              ))}
              <button className="secondary-button allocation-add" type="button" onClick={onAddAllocation}>添加一条新的主教/助教分配</button>
            </div>
          </details>
        </td>
      </tr>
    </>
  );
}

function PlanningFilters({
  label,
  gradeOptions,
  classes,
  teachers,
  courses,
  selectedGrade,
  selectedClass,
  selectedTeacher,
  selectedCourse,
  resultCount,
  onGradeChange,
  onClassChange,
  onTeacherChange,
  onCourseChange
}: {
  label: string;
  gradeOptions: Array<{ code: number; label: string }>;
  classes: ClassList["items"];
  teachers: TeacherList["items"];
  courses: CourseList["items"];
  selectedGrade: string;
  selectedClass: string;
  selectedTeacher: string;
  selectedCourse: string;
  resultCount: number;
  onGradeChange: (value: string) => void;
  onClassChange: (value: string) => void;
  onTeacherChange: (value: string) => void;
  onCourseChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <div className="planning-filters" aria-label={label}>
      <PlanningSearchFilter
        label="年级"
        value={selectedGrade}
        allLabel="全部年级"
        options={gradeOptions.map((grade) => ({
          value: String(grade.code),
          label: grade.label
        }))}
        onChange={onGradeChange}
      />
      <PlanningSearchFilter
        label="班级"
        value={selectedClass}
        allLabel="全部班级"
        options={classes.map((item) => ({
          value: item.id,
          label: `${item.grade_label}${item.class_name}`
        }))}
        onChange={onClassChange}
      />
      <PlanningSearchFilter
        label="教师"
        value={selectedTeacher}
        allLabel="全部教师"
        options={teachers.map((item) => ({ value: item.id, label: item.name }))}
        onChange={onTeacherChange}
      />
      <PlanningSearchFilter
        label="课程"
        value={selectedCourse}
        allLabel="全部课程"
        options={courses.map((item) => ({ value: item.id, label: item.name }))}
        onChange={onCourseChange}
      />
      <div className="planning-filter-summary">
        <strong>{resultCount}</strong>
        <span>项需求</span>
      </div>
    </div>
  );
}

function PlanningSearchFilter({
  label,
  value,
  allLabel,
  options,
  onChange
}: {
  label: string;
  value: string;
  allLabel: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const popupId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const allOptions = [{ value: "ALL", label: allLabel }, ...options];
  const selected = allOptions.find((option) => option.value === value) ?? allOptions[0];
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleOptions = normalizedQuery
    ? allOptions.filter((option) =>
        option.label.toLocaleLowerCase().includes(normalizedQuery)
      )
    : allOptions;

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: PointerEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  function close(restoreFocus = true): void {
    setOpen(false);
    setQuery("");
    if (restoreFocus) triggerRef.current?.focus();
  }

  return (
    <fieldset className="planning-filter-search">
      <legend>{label}</legend>
      <div
        className="multi-select-control"
        ref={rootRef}
        onKeyDown={(event) => {
          if (!open || event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          close();
        }}
      >
        <button
          ref={triggerRef}
          className="multi-select-trigger"
          type="button"
          aria-label={label}
          aria-expanded={open}
          aria-controls={popupId}
          onClick={() => {
            if (open) setQuery("");
            setOpen(!open);
          }}
        >
          <span>{selected.label}</span>
          <small>输入搜索</small>
          <span className="multi-select-chevron" aria-hidden="true">⌄</span>
        </button>
        {open ? (
          <div id={popupId} className="multi-select-popover">
            <div className="multi-select-search">
              <input
                type="search"
                value={query}
                aria-label={`搜索${label}`}
                placeholder={`搜索${label}`}
                autoFocus
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
            <div className="teacher-search-options" role="listbox" aria-label={`${label}列表`}>
              {visibleOptions.length ? (
                visibleOptions.map((option) => (
                  <button
                    className="teacher-search-option"
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    key={option.value}
                    onClick={() => {
                      onChange(option.value);
                      close();
                    }}
                  >
                    <span>{option.label}</span>
                    {option.value === value ? <span aria-hidden="true">✓</span> : null}
                  </button>
                ))
              ) : (
                <p className="multi-select-empty">没有匹配项</p>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </fieldset>
  );
}

function formatLessonRange(minimum: number, maximum: number): string {
  return minimum === maximum ? String(minimum) : `${minimum}～${maximum}`;
}

function LessonRangeField({
  minimum,
  maximum,
  onChange
}: {
  minimum: number;
  maximum: number;
  onChange: (minimum: number, maximum: number) => void;
}): React.JSX.Element {
  return (
    <fieldset className="lesson-range-field">
      <legend>教师课时范围</legend>
      <div className="lesson-range-inputs">
        <input
          type="number"
          min={1}
          max={15}
          required
          aria-label="最少课时"
          value={minimum}
          onChange={(event) => {
            const value = event.currentTarget.valueAsNumber;
            if (!Number.isInteger(value)) return;
            onChange(value, Math.max(value, maximum));
          }}
        />
        <span>至</span>
        <input
          type="number"
          min={1}
          max={15}
          required
          aria-label="最多课时"
          value={maximum}
          onChange={(event) => {
            const value = event.currentTarget.valueAsNumber;
            if (!Number.isInteger(value)) return;
            onChange(Math.min(minimum, value), value);
          }}
        />
      </div>
    </fieldset>
  );
}

function LessonCountField({
  value,
  onChange,
  label = "一周课时数"
}: {
  value: number;
  onChange: (value: number) => void;
  label?: string;
}): React.JSX.Element {
  return (
    <label className="field">
      <span>{label}</span>
      <input
        type="number"
        min={1}
        max={15}
        required
        value={value}
        onChange={(event) => {
          const next = event.currentTarget.valueAsNumber;
          if (Number.isInteger(next)) onChange(next);
        }}
      />
    </label>
  );
}

function WeekPatternField({
  value,
  onChange
}: {
  value: WeekPattern;
  onChange: (value: WeekPattern) => void;
}): React.JSX.Element {
  return (
    <label className="field">
      <span>上课周次</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as WeekPattern)}
      >
        {weekPatternOptions.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function RoomField({
  value,
  rooms,
  onChange
}: {
  value: string;
  rooms: RoomList["items"];
  onChange: (value: string) => void;
}): React.JSX.Element {
  return (
    <label className="field">
      <span>所在功能教室</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">无</option>
        {rooms.map((room) => <option key={room.id} value={room.id}>{room.name}（{room.count} 间）</option>)}
      </select>
      <small className="field-hint">生成时自动选择负载最少的一间具体功能教室。</small>
    </label>
  );
}

function gradeSortValue(gradeCode: number): number {
  const order = [101, 102, 103, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  const index = order.indexOf(gradeCode);
  return index === -1 ? order.length + gradeCode : index;
}

function slotIdsFromCells(cells: AvailableTimeCell[] | null): string[] | null {
  return cells === null
    ? null
    : [...new Set(cells.map((cell) => cell.time_slot_id))];
}

function RuleChecks({
  weeklyLessons,
  consecutive,
  allowMultiple,
  onConsecutive,
  onAllowMultiple
}: {
  weeklyLessons: number;
  consecutive: boolean;
  allowMultiple: boolean;
  onConsecutive: (value: boolean) => void;
  onAllowMultiple: (value: boolean) => void;
}): React.JSX.Element {
  return (
    <div className="plan-rule-options">
      <label className={`check-row ${weeklyLessons === 1 ? "disabled" : ""}`}><input type="checkbox" disabled={weeklyLessons === 1} checked={consecutive} onChange={(event) => onConsecutive(event.target.checked)} /><span>连课<small>按两节紧邻安排；拆给不同主教后，每条分配仍须独立满足此规则。</small></span></label>
      <label className="check-row"><input type="checkbox" checked={allowMultiple} onChange={(event) => onAllowMultiple(event.target.checked)} /><span>同一天可安排多节本课程</span></label>
    </div>
  );
}
